import styled from 'styled-components';
import MaterialIcon from '@/components/icon/MaterialIcon';
import Accordion from '@/components/Accordion';
import { preventArrowKeys } from '@/utils/form';
import SelectBox from '@/components/SelectBox';
import { GSelectOption } from '@/assets/data/selectOptions';
import TextAreaAutoResize from 'react-textarea-autosize';
import { EAlternativeOption, ReadyStatusAgreements } from '@/@types/order';
import theme from '@/styles/theme';

interface Props {
  agreements: ReadyStatusAgreements;
  onSubmitPrice: () => void;
  onPressAgreement: (
    policy: keyof ReadyStatusAgreements,
    isInit?: boolean
  ) => void;
  price: number | string;
  setPrice: (value: React.SetStateAction<number | string>) => void;
  onClickResetOrder: () => void;
  alternativeReason?: GSelectOption<EAlternativeOption>;
  onChangeAlternativeReason: (
    value: GSelectOption<EAlternativeOption>,
    reason?: string
  ) => void;
  alternativeOptions: GSelectOption<EAlternativeOption>[];
  isRequestPaymentDisabled: boolean;
  isPriceInputDisabled: boolean;
  alternativeLog?: string;
  onChangeAlterNativeLog: (value: string) => void;
}
const PrescriptionApprove = ({
  agreements,
  onSubmitPrice,
  onPressAgreement,
  price,
  setPrice,
  onClickResetOrder,
  alternativeReason,
  onChangeAlternativeReason,
  alternativeOptions,
  isRequestPaymentDisabled,
  isPriceInputDisabled,
  alternativeLog,
  onChangeAlterNativeLog
}: Props) => {
  return (
    <div>
      <AccordionRow>
        <Accordion
          title={'대체조제 여부'}
          content={
            <Content>
              <Button
                selected={agreements.isCheckedAlternative === false}
                onClick={() => onPressAgreement('isCheckedAlternative', false)}>
                <MaterialIcon
                  name={'check'}
                  color={theme.DF_COLOR.PRIMARY700}
                />
                처방전 그대로 해요
              </Button>
              <Button
                selected={!!agreements.isCheckedAlternative}
                onClick={() => onPressAgreement('isCheckedAlternative', true)}>
                <MaterialIcon
                  name={'check'}
                  color={theme.DF_COLOR.PRIMARY700}
                />
                대체조제 해요
              </Button>
            </Content>
          }
          isOpen
        />
      </AccordionRow>
      {agreements.isCheckedAlternative && (
        <AccordionRow>
          <Accordion
            title={'대체조제 사유'}
            content={
              <SelectContent>
                <SelectBox
                  customStyles={{
                    singleValue: {
                      whiteSpace: 'pre-wrap'
                    }
                  }}
                  options={alternativeOptions}
                  placeholder={'사유 선택'}
                  onChange={value => {
                    onChangeAlternativeReason(
                      value as GSelectOption<EAlternativeOption>
                    );
                  }}
                />
                {alternativeReason?.value === EAlternativeOption.MANUAL && (
                  <AlternativeInput
                    value={alternativeReason.label}
                    onChange={e =>
                      onChangeAlternativeReason(
                        alternativeOptions[2],
                        e.target.value
                      )
                    }
                    placeholder={'사유를 입력해주세요.'}
                    maxRows={4}
                    maxLength={50}
                    isMarginTop
                  />
                )}
              </SelectContent>
            }
            disabled={!agreements.isCheckedAlternative}
            isOpen={agreements.isCheckedAlternative}
          />
        </AccordionRow>
      )}
      {agreements.isCheckedAlternative && (
        <AccordionRow>
          <Accordion
            title={'대체조제 약품 입력 (선택)'}
            content={
              <Content>
                <AlternativeInput
                  value={alternativeLog}
                  onChange={e => onChangeAlterNativeLog(e.target.value)}
                  placeholder={
                    '기존 약품과 변경된 약품을 입력해주세요 \nex) 피나온 > 제이페시아'
                  }
                  maxRows={4}
                  minRows={2}
                />
              </Content>
            }
            disabled={isPriceInputDisabled}
            isOpen={!isPriceInputDisabled}
          />
        </AccordionRow>
      )}
      <AccordionRow>
        <Accordion
          title={'결제 가격 입력'}
          content={
            <Content>
              <InputBox>
                <PriceInput
                  type="number"
                  placeholder={'ex) 10,000'}
                  value={price}
                  onChange={e => setPrice(Number(e.target.value))}
                  onKeyDown={e => preventArrowKeys(e)}
                  onWheel={event => event.currentTarget.blur()}
                />
                원
              </InputBox>
            </Content>
          }
          disabled={isPriceInputDisabled}
          isOpen={!isPriceInputDisabled}
        />
      </AccordionRow>
      <PaymentButton
        primary
        onClick={onSubmitPrice}
        disabled={isRequestPaymentDisabled}>
        결제 요청하기
      </PaymentButton>
      <ResetOrderTypeButton onClick={onClickResetOrder}>
        <MaterialIcon name={'replay'} size={16} />
        조제접수 초기화
      </ResetOrderTypeButton>
    </div>
  );
};

export default PrescriptionApprove;

const AccordionRow = styled.div`
  ${({ theme }) => `
    border-top: 1px solid ${theme.DF_COLOR.GRAY100};
  `};
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const SelectContent = styled.div`
  margin-bottom: 32px;
`;

const Button = styled.button<{ selected: boolean }>`
  padding: 12px 20px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid;
  display: flex;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
  ${({ theme, selected }) => `
    ${theme.DF_TYPOGRAPHY.UI18SB};
    color: ${selected ? theme.DF_COLOR.PRIMARY700 : theme.DF_COLOR.GRAY600};
    background: ${selected ? theme.DF_COLOR.PRIMARY100 : theme.DF_COLOR.WHITE};
    border-color: ${
      selected ? theme.DF_COLOR.PRIMARY100 : theme.DF_COLOR.GRAY100
    };
    div {
      margin-right: 8px;
      display: ${selected ? 'flex' : 'none'};
    }
  `};
`;

const InputBox = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  margin-top: 8px;
  gap: 8px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI20R};
    color: ${theme.DF_COLOR.GRAY900};
  `};
`;

const PriceInput = styled.input`
  padding: 14px 16px;
  text-align: right;
  border-radius: 4px;
  flex: 1;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    color: ${theme.DF_COLOR.GRAY900};
    border: 1px solid ${theme.DF_COLOR.GRAY100};
  `};
`;

const PaymentButton = styled.button<{ primary?: boolean }>`
  margin-top: 8px;
  padding: 16px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  border: 1px solid;

  ${({ theme, primary }) => `
    ${theme.DF_TYPOGRAPHY.UI16SB};
    color: ${primary ? theme.DF_COLOR.WHITE : theme.DF_COLOR.GRAY600};
    background: ${primary ? theme.DF_COLOR.PRIMARY500 : theme.DF_COLOR.WHITE};
    border-color: ${
      primary ? theme.DF_COLOR.PRIMARY500 : theme.DF_COLOR.GRAY200
    }};
    
    &:hover:not(:disabled) {
      opacity: 0.7;
    }
  
    :disabled {
      background: ${theme.DF_COLOR.GRAY100};
      border-color: ${theme.DF_COLOR.GRAY100};
      cursor: default;
    }
  `};
`;

const ResetOrderTypeButton = styled.button`
  width: 100%;
  padding: 4px;
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;

  div {
    margin-right: 8px;
  }
  &:hover {
    cursor: pointer;
  }

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI14M};
    color: ${theme.DF_COLOR.GRAY600};
  `};
`;

const AlternativeInput = styled(TextAreaAutoResize)<{ isMarginTop?: boolean }>`
  border-radius: 4px;
  width: 100%;
  resize: none;
  outline: none;

  ${({ theme, isMarginTop }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    ${isMarginTop && 'margin-top: 12px'};
    color: ${theme.DF_COLOR.GRAY700};
    border: 1px solid ${theme.DF_COLOR.GRAY100};
    padding: 14px 16px;

    ::placeholder {
      color: ${theme.DF_COLOR.GRAY200};
    }
  `};
`;
